/**
 * Enquiry service — wired to the REAL backend. Keeps the reference UI's validation + honeypot seam,
 * but `submitEnquiry` POSTs to the live public inquiry API (`/public/inquiries`) with the enquiry
 * `type` (PRODUCT / RETAILER / DISTRIBUTOR / GENERAL) added in Phase 0, so retailer/distributor/contact
 * leads flow through the SAME pipeline as product inquiries and land in the dashboard. Nothing else in
 * the UI changed — this is the single integration seam the reference UI was designed around.
 */
import { request } from '../../api/client';
import { productRepository } from './productRepository';
import { isValidPhoneNumber } from 'libphonenumber-js';

export type EnquiryType = 'product' | 'farmer' | 'retailer' | 'distributor' | 'general';

/**
 * WHO the visitor is — a different question from what they are enquiring ABOUT.
 *
 * Without it the backend filed every unknown phone number as a farmer, so a dealer asking about
 * stock became a farmer record; one number in the live data is now both. Asking is the whole fix.
 */
export type EnquirerType = 'farmer' | 'retailer' | 'distributor' | 'other';

export interface EnquiryPayload {
  type: EnquiryType;
  /** Who they are. Optional on the wire so an older client still submits; the form asks for it. */
  enquirerType?: EnquirerType;
  name: string;
  email?: string;
  phone: string;
  location?: string;
  /** Their pincode. This is what lets a contact-form enquiry be ROUTED — see the submit comment. */
  pincode?: string;
  productSlug?: string;
  message: string;
  /** honeypot — must be empty */
  company?: string;
}

export interface EnquiryResult {
  ok: true;
  reference: string;
}

export class EnquiryValidationError extends Error {
  fields: Record<string, string>;
  constructor(fields: Record<string, string>) {
    super('Please correct the highlighted fields.');
    this.name = 'EnquiryValidationError';
    this.fields = fields;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone punctuation only, AND a real number for its country (a bare number is read as Indian) —
// the old 7-to-20-character pattern let "1234567890000000" through to be refused by the server.
const PHONE_RE = /^[+()\-\s\d]{7,20}$/;
const validPhone = (v: string) => PHONE_RE.test(v.trim()) && isValidPhoneNumber(v.trim(), 'IN');

export function validateEnquiry(payload: Partial<EnquiryPayload>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!payload.name || payload.name.trim().length < 2) errors.name = 'Please enter your name.';
  if (!payload.phone || !validPhone(payload.phone)) errors.phone = 'Please enter a valid phone number.';
  if (payload.email && !EMAIL_RE.test(payload.email)) errors.email = 'Please enter a valid email address.';
  // Optional, but if given it must be a real Indian PIN — a half-typed one routes nowhere and the
  // visitor would never learn why their enquiry went to the central queue.
  // `\d`, not `d`: without the backslash the pattern wanted a digit then five letter d's, so EVERY real
  // pincode was refused and a contact-form enquiry could never carry the one field that routes it
  // (certification DEF-038).
  if (payload.pincode && !/^[1-9]\d{5}$/.test(payload.pincode.trim())) errors.pincode = 'A pincode is 6 digits.';
  if (!payload.message || payload.message.trim().length < 10)
    errors.message = 'Please add a few words about your enquiry (min. 10 characters).';
  return errors;
}

/** Map the UI enquiry type to the backend triage type. */
function backendType(t: EnquiryType): 'PRODUCT' | 'RETAILER' | 'DISTRIBUTOR' | 'GENERAL' {
  if (t === 'product') return 'PRODUCT';
  if (t === 'retailer') return 'RETAILER';
  if (t === 'distributor') return 'DISTRIBUTOR';
  return 'GENERAL'; // general + farmer
}

export async function submitEnquiry(payload: EnquiryPayload): Promise<EnquiryResult> {
  // Honeypot: a bot filled the hidden field → silently accept-and-drop (the backend also honeypots).
  if (payload.company && payload.company.trim() !== '') {
    return { ok: true, reference: 'GB-000000' };
  }

  const errors = validateEnquiry(payload);
  if (Object.keys(errors).length > 0) throw new EnquiryValidationError(errors);

  const product = payload.productSlug ? productRepository.getProductBySlugSync(payload.productSlug) : undefined;
  const messageParts = [payload.message.trim()];
  if (payload.type === 'farmer') messageParts.unshift('[Farmer enquiry]');
  if (payload.location?.trim()) messageParts.push(`Location: ${payload.location.trim()}`);

  const res = await request<{ inquiryNo: string; publicToken: string; status: string }>({
    url: '/public/inquiries',
    method: 'POST',
    data: {
      type: backendType(payload.type),
      customerName: payload.name.trim(),
      customerEmail: payload.email?.trim() || undefined,
      customerPhone: payload.phone.trim(),
      // "Not specific / multiple" is the form's own default for a Product enquiry. Sending no product at all made
      // the server refuse it (PRODUCT_REQUIRED) and the lead was lost behind "Something went wrong" — so a
      // product enquiry about no single product says so in words (certification DEF-041).
      productNameText: product?.name || (payload.type === 'product' ? 'Several products (not specified)' : undefined),
      city: payload.location?.trim() || undefined,
      /*
       * The two fields that make a contact-form enquiry a FIRST-CLASS one.
       *
       * Until now this form collected a free-text "Location / District" and nothing else, so every
       * enquiry it produced was unroutable — a "Product enquiry" raised here could never reach a rep,
       * while the same enquiry raised on a product page could. The pincode gives the server something
       * to route on; `enquirerType` stops the backend guessing who the person is.
       */
      pincode: payload.pincode?.trim() || undefined,
      enquirerType: payload.enquirerType ? payload.enquirerType.toUpperCase() : undefined,
      message: messageParts.join(' — '),
      website: payload.company || undefined, // honeypot passthrough
    },
  });

  return { ok: true, reference: res.inquiryNo };
}
